import apiClient from '../api/apiClient';

class AuthService {
  constructor() {
    this.user = null;
    // Configuration could be loaded from an API endpoint or environment
    this.config = {
      clientId: window._env_.REACT_APP_CLIENT_ID || 'gs-auth',
      authority:
        window._env_.REACT_APP_AUTH_AUTHORITY || 'https://connect.identity.stagaws.visma.com',
      scopes: ['openid', 'profile', 'email', 'tenants', 'roles'],
    };
  }

  async initiateLogin() {
    // Generate PKCE code verifier and hash it for the code challenge
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // Store code verifier for token exchange
    sessionStorage.setItem('code_verifier', codeVerifier);

    // Generate random state for CSRF protection
    const state = this.generateRandomState();
    sessionStorage.setItem('auth_state', state);

    // Store intended destination for post-login redirect
    const path = window.location.pathname;
    if (path !== '/login' && path !== '/auth/callback') {
      sessionStorage.setItem('auth_redirect', path);
    }

    // Get redirect URI
    const redirectUri = window.location.origin + '/auth/callback';

    // Construct query parameters
    const queryParams = {
      client_id: this.config.clientId,
      response_type: 'code',
      scope: this.config.scopes.join(' '),
      redirect_uri: redirectUri,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: state,
      response_mode: 'query',
    };

    // Build the query string
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    // Redirect to authorization endpoint
    window.location.href = `${this.config.authority}/connect/authorize?${queryString}`;
  }

  async handleAuthCallback(code, state) {
    // Verify state
    const storedState = sessionStorage.getItem('auth_state');
    sessionStorage.removeItem('auth_state');

    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }

    // Get code verifier
    const codeVerifier = sessionStorage.getItem('code_verifier');
    sessionStorage.removeItem('code_verifier');

    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    try {
      // Exchange code for tokens - tokens will be set as HttpOnly cookies by the backend
      await apiClient.post('/api/auth/token', {
        code,
        redirect_uri: window.location.origin + '/auth/callback',
        code_verifier: codeVerifier,
      });

      // Get user info from a new endpoint
      const user = await this.fetchUserInfo();
      this.user = user;

      // Set authenticated flag in session storage
      sessionStorage.setItem('isAuthenticated', 'true');

      return user;
    } catch (error) {
      console.error('Error during token exchange:', error);
      throw error;
    }
  }

  async fetchUserInfo() {
    try {
      // Call user info endpoint, cookies will be sent automatically
      const response = await apiClient.get('/api/auth/userinfo');
      const userInfo = response.data;
      this.user = userInfo;
      return userInfo;
    } catch (error) {
      console.error('Error fetching user info:', error);
      return null;
    }
  }

  async checkActivationStatus() {
    try {
      const response = await apiClient.post('/api/auth/check-activation-status');
      return response.data;
    } catch (error) {
      console.error('Error checking activation status:', error);
      throw error;
    }
  }

  async getActivationRedirectUrl(returnUrl = null) {
    try {
      const params = returnUrl ? { returnUrl } : {};
      const response = await apiClient.get('/api/auth/activation-redirect-url', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting activation redirect URL:', error);
      throw error;
    }
  }

  async completeActivation(activationToken, additionalData = null) {
    try {
      const response = await apiClient.post('/api/auth/complete-activation', {
        activationToken,
        additionalData,
      });
      return response.data;
    } catch (error) {
      console.error('Error completing activation:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    // If user is already loaded, return it
    if (this.user) {
      return this.user;
    }

    // Check if user is authenticated
    if (sessionStorage.getItem('isAuthenticated') === 'true') {
      // Fetch user info from API
      return await this.fetchUserInfo();
    }

    return null;
  }

  async ssoLogout() {
    try {
      const response = await apiClient.post('/api/auth/sso-logout');

      this.user = null;
      sessionStorage.removeItem('isAuthenticated');

      if (response.data?.endSessionUrl) {
        window.location.href = response.data.endSessionUrl;
      } else {
        this.redirectToLogin();
      }
    } catch (error) {
      console.error('SSO logout error:', error);
      this.user = null;
      sessionStorage.removeItem('isAuthenticated');
      this.redirectToLogin();
    }
  }

  // NOTE: Refresh token functionality is disabled
  // This method is kept as a stub but will not be called by the application
  async refreshToken() {
    console.warn('Refresh token functionality is disabled');
    return false;
  }

  async logout() {
    try {
      // Call logout endpoint to clear cookies and get SSO end-session URL
      const response = await apiClient.post('/api/auth/logout');

      // Clear user data
      this.user = null;
      sessionStorage.removeItem('isAuthenticated');

      // Redirect to Visma Connect end-session endpoint to clear SSO session,
      // which will redirect back to /login after clearing the SSO cookie
      if (response.data?.endSessionUrl) {
        window.location.href = response.data.endSessionUrl;
      } else {
        this.redirectToLogin();
      }
    } catch (error) {
      console.error('Logout error:', error);

      // Even if the API call fails, clear local state and redirect
      this.user = null;
      sessionStorage.removeItem('isAuthenticated');
      this.redirectToLogin();
    }
  }

  // Helper method to redirect to login page
  redirectToLogin() {
    // If we're not already on the login page, redirect
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // PKCE helper methods
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  base64UrlEncode(buffer) {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(digest);
  }

  generateRandomState() {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
